import { Modal, Select, Spin, notification } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import { ReactNode, useState } from 'react';
import { API_BASE_URL } from '../config';
import { Callout, CalloutTone, UploadDropzone } from './ui';

export interface ImportModalConfig {
  title: string;
  description: string;
  /** Relative path (resolved against API_BASE_URL) or absolute URL. */
  action: string;
  successMessage: string;
  errorMessage: string;
  callout?: { tone: CalloutTone; title?: string; body: ReactNode };
  /** CSV columns the upload needs, shown with a sample file. */
  columns?: ImportColumn[];
  /** Parse the upload response and return a list of skipped/unknown domains. */
  parseSkipped?: (response: unknown) => string[];
  /** Invoked on successful upload, after notification fires. */
  onUploaded?: (response: unknown) => void;
}

export interface ImportColumn {
  header: string;
  description: string;
  example: string;
}

export interface ImportChoice {
  key: string;
  label: string;
  config: ImportModalConfig;
}

interface Props {
  config: ImportModalConfig;
  onClose: () => void;
}

interface ChoiceProps {
  title: string;
  description: string;
  choices: ImportChoice[];
  onClose: () => void;
}

function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** A header line and one example row for the given columns. */
export function sampleCSV(columns: ImportColumn[]): string {
  return [columns.map((c) => csvCell(c.header)), columns.map((c) => csvCell(c.example))]
    .map((row) => row.join(','))
    .join('\n');
}

/** The server's `{ error }` message for a rejected upload, when it sent one. */
export function uploadErrorDetail(response: unknown): string | undefined {
  if (response && typeof response === 'object' && 'error' in response) {
    const error = (response as { error: unknown }).error;
    if (typeof error === 'string') return error;
  }
  return undefined;
}

function resolveAction(action: string): string {
  if (action.startsWith('http://') || action.startsWith('https://')) return action;
  return API_BASE_URL + action;
}

/** Status text for an upload in flight: sending the file, then waiting on the server. */
export function uploadProgressLabel(fileName: string, percent: number | undefined): string {
  if (percent !== undefined && percent < 100) return `Uploading ${fileName}… ${Math.round(percent)}%`;
  return `Processing ${fileName}…`;
}

function ImportBody({ config }: { config: ImportModalConfig }) {
  const [skipped, setSkipped] = useState<string[] | null>(null);
  const [inFlight, setInFlight] = useState<{ name: string; percent?: number } | null>(null);

  function onChange(info: UploadChangeParam<UploadFile<any>>) {
    if (info.file.status === 'uploading') {
      setInFlight({ name: info.file.name, percent: info.file.percent });
      return;
    }
    setInFlight(null);
    if (info.file.status === 'done') {
      try {
        if (config.parseSkipped) {
          setSkipped(config.parseSkipped(info.file.response));
        }
        notification.success({ message: config.successMessage });
        config.onUploaded?.(info.file.response);
      } catch {
        notification.error({ message: 'Failed to process response.' });
      }
    } else if (info.file.status === 'error') {
      notification.error({ message: config.errorMessage, description: uploadErrorDetail(info.file.response) });
    }
  }

  function onDrop() {
    setSkipped(null);
  }

  return (
    <>
      {config.columns && <RequiredColumns columns={config.columns} />}
      {config.callout && (
        <Callout tone={config.callout.tone} title={config.callout.title}>
          {config.callout.body}
        </Callout>
      )}
      <UploadDropzone
        action={resolveAction(config.action)}
        onChange={onChange}
        onDrop={onDrop}
        disabled={inFlight !== null}
      />
      {inFlight && (
        <div role="status" className="flex items-center gap-2 text-text-2 text-[12.5px]">
          <Spin size="small" />
          {uploadProgressLabel(inFlight.name, inFlight.percent)}
        </div>
      )}
      {skipped && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1.5">
            Domains Skipped ({skipped.length})
          </div>
          {skipped.length === 0 ? (
            <p className="text-text-3 text-[12.5px]">None</p>
          ) : (
            <ul className="font-mono text-[11.5px] text-text-2 bg-bg-elevated border border-border rounded-md p-3 max-h-[260px] overflow-auto">
              {skipped.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

const sectionLabel = 'text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1.5';

function RequiredColumns({ columns }: { columns: ImportColumn[] }) {
  return (
    <div>
      <div className={sectionLabel}>Required columns</div>
      <table className="w-full text-[12.5px] border border-border rounded-md overflow-hidden" aria-label="Required columns">
        <tbody>
          {columns.map((c) => (
            <tr key={c.header} className="border-b border-border last:border-b-0">
              <td className="px-3 py-1.5 font-mono text-text whitespace-nowrap align-top">{c.header}</td>
              <td className="px-3 py-1.5 text-text-3">{c.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={`${sectionLabel} mt-3`}>Example</div>
      <pre className="font-mono text-[11.5px] text-text-2 bg-bg-elevated border border-border rounded-md p-3 m-0 overflow-auto">
        {sampleCSV(columns)}
      </pre>
    </div>
  );
}

function ImportShell({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={560}
      closable={false}
      destroyOnClose
      styles={{
        content: { padding: 0, background: 'rgb(var(--color-panel) / 1)', borderRadius: 10, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      <div className="px-6 pt-5 pb-4 border-b border-border bg-panel flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1">Import</div>
          <div className="text-text text-[17px] font-semibold leading-tight">{title}</div>
          <div className="text-text-3 text-[12.5px] mt-1">{description}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text flex-shrink-0"
        >
          ✕
        </button>
      </div>
      <div className="px-6 py-5 bg-bg flex flex-col gap-4">{children}</div>
    </Modal>
  );
}

export function ImportModal({ config, onClose }: Props) {
  return (
    <ImportShell title={config.title} description={config.description} onClose={onClose}>
      <ImportBody config={config} />
    </ImportShell>
  );
}

/** One import modal for several upload kinds, chosen from a dropdown. */
export function ImportChoiceModal({ title, description, choices, onClose }: ChoiceProps) {
  const [selectedKey, setSelectedKey] = useState(choices[0]?.key);
  const selected = choices.find((c) => c.key === selectedKey);

  return (
    <ImportShell title={title} description={description} onClose={onClose}>
      <div>
        <div className={sectionLabel} id="import-choice-label">
          Data to import
        </div>
        <Select
          aria-labelledby="import-choice-label"
          className="w-full"
          value={selectedKey}
          onChange={setSelectedKey}
          options={choices.map((c) => ({ value: c.key, label: c.label }))}
        />
        {selected && <div className="text-text-3 text-[12.5px] mt-1.5">{selected.config.description}</div>}
      </div>
      {selected && <ImportBody key={selected.key} config={selected.config} />}
    </ImportShell>
  );
}
